import { FormControl, MenuItem, Select } from '@mui/material'
import { Hall } from '@weblite/ts-action/hall'
import { useEffect, useState } from 'react'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { Workspace } from '@weblite/ts-action/workspace'
import { Room } from '@weblite/ts-action/room'
// Localization
import { t } from '$i18n'
// Logic
import { nothing } from '$helper/functions/utils'
import { changeHallInfo } from '$logic/hall'
// Common
import { Switch } from '$common/switch'
import { OptionWithTextItem } from '$common/option-with-text-item'
import { showSaveChangesLoadingModal } from '$comp/modals/loading-modal'
import { addWorkspace, getWorkspaces, searchWorkspaces } from '$logic/workspace'
import SelectWithChips from '$common/select-with-chips/select-with-chips'
import { SettingsSection } from '$common/settings-section'
import { PREMIUM_PLAN_TAG } from '$logic/hall/constant'

const changeHallInfoSync = async (
  roomId: string,
  patch: Partial<Pick<Hall, 'tags' | 'verified'>>,
) => {
  showSaveChangesLoadingModal()
  await changeHallInfo(roomId, patch)
  await closeCurrentModal()
}

// tags
const COURSE_VITRINE = 'course_vitrine_integrated'
const LIVE_WEBRTC = 'LIVE_WEBRTC'
const LIVE_INTEGRATED = 'live_integrated'
const CLASSROOM_HLS = 'CLASSROOM_HLS'
const CLASSROOM_RTMP = 'CLASSROOM_RTMP'
const VOD_ACTIVATED = 'vod_activated'

const classTypes = ['', 'WebRTC', 'HLS', 'RTMP'] as const
type ClassType = (typeof classTypes)[number]

const getClassType = (tags: string[]): ClassType => {
  if (!tags.includes(LIVE_INTEGRATED)) return ''

  if (tags.includes(CLASSROOM_HLS)) return 'HLS'
  if (tags.includes(CLASSROOM_RTMP)) return 'RTMP'
  return 'WebRTC'
}

const setClassType = (tags: string[], type: ClassType) => {
  const newTags = tags.filter(
    tag => ![LIVE_INTEGRATED, CLASSROOM_HLS, CLASSROOM_RTMP].includes(tag),
  )
  if (!type) return newTags

  newTags.push(LIVE_INTEGRATED)
  if (type === 'HLS') newTags.push(CLASSROOM_HLS)
  if (type === 'RTMP') newTags.push(CLASSROOM_RTMP)
  return newTags
}

export function HallSettingsSuperUserActions({
  hall,
  room,
}: {
  hall: Hall
  room: Room | undefined
}) {
  if (!hall) return null
  const { verified, roomId } = hall
  const tags = hall.tags || []

  return (
    <>
      <OptionWithTextItem
        primaryText="Online Class Type"
        secondaryText=""
        icon={
          <FormControl>
            <Select
              value={getClassType(tags)}
              displayEmpty
              onChange={e =>
                changeHallInfoSync(roomId, {
                  tags: setClassType(tags, e.target.value as ClassType),
                })
              }
              autoWidth
            >
              {classTypes.map(type => (
                <MenuItem key={type} value={type}>
                  {type || <em>None</em>}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        }
        onClick={nothing}
      />
      <SwitchButtonItem
        label="Course Vitrine"
        checked={tags?.includes(COURSE_VITRINE)}
        onClick={() =>
          changeHallInfoSync(roomId, {
            tags: tags?.includes(COURSE_VITRINE)
              ? tags.filter(tag => tag !== COURSE_VITRINE)
              : [...tags, COURSE_VITRINE],
          })
        }
      />
      <SwitchButtonItem
        label="WebRTC Live"
        checked={tags?.includes(LIVE_WEBRTC)}
        onClick={() =>
          changeHallInfoSync(roomId, {
            tags: tags?.includes(LIVE_WEBRTC)
              ? tags.filter(tag => tag !== LIVE_WEBRTC)
              : [...tags, LIVE_WEBRTC],
          })
        }
      />
      <SwitchButtonItem
        label="VOD"
        checked={tags?.includes(VOD_ACTIVATED)}
        onClick={() =>
          changeHallInfoSync(roomId, {
            tags: tags?.includes(VOD_ACTIVATED)
              ? tags.filter(tag => tag !== VOD_ACTIVATED)
              : [...tags, VOD_ACTIVATED],
          })
        }
      />

      <SwitchButtonItem
        label="Premium Plan"
        checked={tags?.includes(PREMIUM_PLAN_TAG)}
        onClick={() =>
          changeHallInfoSync(roomId, {
            tags: tags?.includes(PREMIUM_PLAN_TAG)
              ? tags.filter(tag => tag !== PREMIUM_PLAN_TAG)
              : [...tags, PREMIUM_PLAN_TAG],
          })
        }
      />

      <SwitchButtonItem
        label={t('common.verified')}
        checked={verified}
        onClick={() => changeHallInfoSync(roomId, { verified: !verified })}
      />

      {!!room && <SuperUserWorkspaceSelection room={room} />}
    </>
  )
}

interface SwitchButtonItemProps {
  checked?: boolean
  onClick: () => Promise<unknown>
  label: string
}

export function SwitchButtonItem({
  onClick,
  checked = false,
  label,
}: SwitchButtonItemProps) {
  const [disabled, setDisabled] = useState(false)
  return (
    <OptionWithTextItem
      primaryText={label}
      secondaryText=""
      icon={<Switch checked={checked} disabled={disabled} />}
      onClick={() => {
        setDisabled(true)
        onClick().finally(() => setDisabled(false))
      }}
    />
  )
}

function SuperUserWorkspaceSelection({ room }: { room: Room }) {
  const [workspaceSelectorIsOpen, setWorkspaceSelectorIsOpen] = useState(false)
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [hallWorkSpace, setHallWorkspace] = useState<Workspace | null>(null)

  useEffect(() => {
    const fetchWorkSpaces = async () => {
      const res = await searchWorkspaces()
      setWorkspaceSelectorIsOpen(false)
      if (res.error) {
        console.warn(res.error)
        return
      }
      setWorkspaces([{ name: 'none', id: 'none' }, ...res.payload.workspaces])
    }
    if (workspaceSelectorIsOpen) fetchWorkSpaces()
  }, [workspaceSelectorIsOpen, room])

  useEffect(() => {
    const fetchRoomWorkSpace = async () => {
      if (!room.workspaceId) return setHallWorkspace(null)
      const res = await getWorkspaces({ workspaceIds: [room.workspaceId] })
      if (res.error) {
        console.warn(res.error)
        return
      }
      setHallWorkspace(res.payload.workspaces[0])
    }
    fetchRoomWorkSpace()
  }, [room])

  const setChannelWorkspace = async (workspaceId: string) => {
    const res = await changeHallInfoSync(room.id, {
      workspaceId: workspaceId === 'none' ? null : workspaceId,
    })
    if (res?.error) {
      console.warn(res.error)
      return
    }
    await addWorkspace(workspaceId)
  }

  return (
    <SettingsSection headerText="Work space list">
      <SelectWithChips
        id="SELECT_HALL_WORKSPACE"
        options={workspaces}
        value={hallWorkSpace}
        onChange={item => setChannelWorkspace(item.id)}
        getOptionLabel={item => item.name}
        fullWidth
        onOpen={() => setWorkspaceSelectorIsOpen(true)}
      />
    </SettingsSection>
  )
}
